import React, { useState } from "react";
import { Typography, Modal, Box, Grid } from "@mui/material";
import apiClient from "../utils/apiClient";
import CustomButton from "./custom/CustomButton";
import CustomNumberField from "./custom/CustomNumberField";
import { isKtpValid } from "../utils/ValidationRules";
import { useUser, useUserActions } from "../pages/UserContext";
import { showSuccessToast, showErrorToast, showWarningToast } from "../utils/swalToast";

// App-wide block for members who haven't confirmed their KTP — mounted once
// in Layout so it applies no matter which page they land on, instead of only
// nudging on /member-card while every other route stayed fully usable.
// Still shown while impersonating (so an admin can actually fix a member's
// KTP through this same, correctly-wired form), but dismissible in that case
// so debugging isn't blocked by a wall the admin doesn't need to clear.
const KtpUpdateGate = () => {
  const userDetails = useUser();
  const { refetchUser } = useUserActions();
  const [newNik, setNewNik] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isImpersonating = !!userDetails?.impersonator;

  const needsKtpUpdate =
    !!userDetails &&
    userDetails.is_member &&
    userDetails.is_ktp_updated !== true &&
    !(isImpersonating && dismissed);

  if (!needsKtpUpdate) return null;

  const handleUpdateNik = () => {
    if (newNik.length !== 16) {
      showWarningToast("NIK harus terdiri dari 16 digit.");
      return;
    }

    setIsLoading(true);
    apiClient
      .put("/users/ktp-update", { nik: newNik, email: userDetails.email })
      .then(async () => {
        showSuccessToast("Pembaharuan NIK user berhasil.");
        setNewNik("");
        await refetchUser();
      })
      .catch((error) => {
        showErrorToast("Gagal memperbarui KTP. Coba lagi.");
        console.error(error);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Modal
      open
      disableEscapeKeyDown={!isImpersonating}
      onClose={(event, reason) => {
        if (reason === "backdropClick" && !isImpersonating) return;
        if (isImpersonating) setDismissed(true);
      }}
      aria-labelledby="modal-ktp"
      aria-describedby="update-ktp-form"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          maxWidth: "90vw",
          p: 4,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
        }}
      >
        <Grid container spacing={2}>
          <Grid item md={12} xs={12}>
            <Typography variant="h5" sx={{ marginBottom: "20px" }}>
              Pembaharuan Nomor KTP
            </Typography>
            <Typography variant="body2" color="error" sx={{ fontWeight: "bold" }}>
              *Untuk memastikan keakuratan NIK yang tersimpan dalam sistem, pengguna dihimbau
              untuk memasukkan kembali NIK agar program dapat menghasilkan kartu anggota dengan
              benar.
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: "8px",
                backgroundColor: "#f5f7fa",
                border: "1px dashed #003158",
              }}
            >
              <Typography sx={{ fontSize: "0.95em", color: "#555" }}>NIK Lama:</Typography>
              <Typography sx={{ fontSize: "0.95em", fontWeight: 700, color: "#003158" }}>
                {userDetails.ktp}
              </Typography>
            </Box>
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography variant="body2" color="error" sx={{ fontWeight: "bold" }}>
              Jika NIK lama yang tersimpan sudah benar, cukup salin dan tempelkan ke dalam text
              input di bawah.
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <CustomNumberField
              value={newNik}
              rule={isKtpValid}
              onChange={(event) => setNewNik(event.target.value)}
              maxInputLength={16}
            />
          </Grid>
          <Grid item md={isImpersonating ? 6 : 12} xs={12}>
            <CustomButton
              text={isLoading ? "Memperbarui..." : "Update"}
              onClick={handleUpdateNik}
              baseColor="#003158"
              hoverColor="#00213A"
              disabled={!isKtpValid(newNik).isValid}
            />
          </Grid>
          {isImpersonating && (
            <>
              <Grid item md={6} xs={12}>
                <CustomButton
                  text="Lewati untuk sekarang"
                  onClick={() => setDismissed(true)}
                  baseColor="#A92027"
                  hoverColor="#8C1016"
                />
              </Grid>
              <Grid item md={12} xs={12}>
                <Typography variant="body2" sx={{ color: "#555" }}>
                  Jika dilewati, anggota masih bisa memperbarui NIK-nya melalui menu Edit Profile.
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Modal>
  );
};

export default KtpUpdateGate;
